/* eslint import/no-import-module-exports: "off" */
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { initialState as intlInitialState } from '../../components/IntlRedux';
import IntlProvider from '../../components/IntlRedux/IntlProvider';
import MessageHolder from '../../components/MessageHolder';
import ModalHolder from '../../components/ModalHolder';

import API from '../../helpers/api';
import { getCookie, setCookie } from '../../helpers/cookie';
import { toIntlLocale } from '../../helpers/lang';

import { loadLoginDataIfNeeded } from '../../modules/client/actions/Client';
import dataActions from '../../modules/data/actions';
import createStore from '../../store';
import AntdProviders from '../AntdProviders';
import RenderRoutes from '../RenderRoutes';
import routes from '../routes';

const EVERY_PAGE_LIB_PATH = '/every-page-lib.js';

const loadEveryPageLib = () => {
  if (document.querySelector('script[src*="every-page-lib.js"]')) return;

  try {
    const script = document.createElement('script');
    script.src = API.getProfileUrl(EVERY_PAGE_LIB_PATH);
    script.type = 'text/javascript';
    document.head.appendChild(script);
  } catch (err) {
    console.error('Failed to load every-page-lib:', err);
  }
};

window.__SERVER__ = false;
window.__CLIENT__ = true;

dayjs.extend(utc);
dayjs.extend(timezone);

const initialState = window.__INITIAL_STATE__;
const storedLang = getCookie('lang');
const initialLang = initialState?.intl?.locale || storedLang || 'en';
const initialLocale = toIntlLocale(initialLang);
const storedTheme = getCookie('themeType');
const initialTheme =
  storedTheme === 'light' || storedTheme === 'dark' ? storedTheme : 'dark';
const initialMessages =
  initialState?.intl?.messages ?? intlInitialState.messages;

const preloadedState = {
  ...initialState,
  intl: {
    ...intlInitialState,
    ...initialState?.intl,
    locale: initialLocale,
    messages: initialMessages,
  },
};

delete preloadedState.router;

const { store } = createStore({
  initialState: preloadedState,
  lang: initialLocale,
});

setCookie('lang', initialLocale);
setCookie('themeType', initialTheme);
document.documentElement.dataset.theme = initialTheme;

const render = (renderRoutes: any[]) => {
  const container = document.getElementById('react-view');

  if (!container) {
    return;
  }

  if (window.__SSR__ === undefined) {
    window.__SSR__ = container.childNodes.length > 0;
  }

  const app = (
    <AntdProviders>
      <Provider store={store}>
        <IntlProvider textComponent="span">
          <BrowserRouter>
            <RenderRoutes routes={renderRoutes} />
          </BrowserRouter>
          <ModalHolder />
          <MessageHolder />
        </IntlProvider>
      </Provider>
    </AntdProviders>
  );

  if (window.__SSR__) {
    hydrateRoot(container, app);
    return;
  }

  if (!window.__ROOT__) {
    window.__ROOT__ = createRoot(container);
  }

  window.__ROOT__.render(app);
};

const bootstrap = async () => {
  const hasSsrState = Boolean(initialState);

  if (hasSsrState) {
    render(routes);
  }

  try {
    await store.dispatch(dataActions.Data.loadIfNeeded());
    loadEveryPageLib();
  } catch (err) {
    console.error('Failed to load initial data:', err);
  }

  try {
    await store.dispatch(loadLoginDataIfNeeded());
  } catch (err) {
    console.error('Failed to load login data:', err);
  }

  try {
    await store.dispatch(dataActions.Data.loadLangIfNeeded());
  } catch (err) {
    console.error('Failed to load language data:', err);
  }

  if (!hasSsrState) {
    render(routes);
  }
};

void bootstrap();

if (import.meta.hot) import.meta.hot.accept();
